//=====================================================
//　G006：予約確認
//=====================================================

import { useContext, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { ContentsBox } from "../../common/ContentsBox";
import { BtnSubmit } from "../../common/BtnSubmit";
import { ReservationFlow, ProcessName } from "../../common/ReservationFlow";
import { ContentsTitle } from "../../common/ContentsTitle";
import { MsgBoxErr } from '../../common/MsgBoxErr';
import { ReservationInfo } from "../../common/ReservationInfo";
import { BtnReturn } from "../../common/BtnReturn";
import { useAuth } from "../../utils/UseAuth";
import { CtxClinicInfo, CtxNewReservation, CtxPatientInfo, CtxSystemErr } from "../../utils/Contexts";
import { postApiData, postApiDataWithToken, putApiDataWithToken } from "../../utils/CallApi";
import { ReservationType } from "../../utils/ConstCommon";
import { formatDateTime } from "../../utils/CommonFunc";
import { AxiosResponse } from "axios";
import { ConstMsg } from "../../utils/ConstMsg";
import { LoadingBox } from "../../common/LoadingBox";
import { LoginStatusType } from "../../utils/ConstCommon"

export function G006(){
    const [errmess, setErrmess] = useState("");
    const history = useHistory();
    const cserr = useContext(CtxSystemErr);
    const cnr = useContext(CtxNewReservation);
    const cci = useContext(CtxClinicInfo);
    const cpi = useContext(CtxPatientInfo);
    const ua = useAuth();
    const [loadingDone, setLoadingDone] = useState(true);

    // ブラウザバックで表示させない処理
    useEffect(function(){
        //タイトル追加処理
        if(cci.data.name){
            document.title = cci.data.name + " | Web予約 - 予約確認";
        }else{
            document.title = process.env.REACT_APP_DEFAULT_CLININC_NAME + " | Web予約 - 予約確認";
        }
        
        // マウントフラグ
        let isMounted = true;
        
        // アンマウント時はstate(history)を更新しないようにする
        if(isMounted){
            // 完了データの場合、マイページ、トップ画面へ遷移
            if(cnr.data.done){
                if(ua.isLoggedIn()){
                    history.push('/mypage');
                } else {
                    history.push('/');
                }
            }
        }

        // cleanup関数(Unmount時の処理)
        return () => {
            // マウントオフ
            isMounted = false;
        }
        // アンマウント時にのみ一回だけ実行したいため第2引数指定なし。
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const token = ua.getToken();
    const patientNo = ua.getPatientNo();
    const clinicId = cci.data.id;

    // 整形された日時文字列の配列["yyyy/mm/dd", "hh:ii"]を取得
    const aryDateTime = cnr.data.date ? formatDateTime(cnr.data.date).split(" ") : ["",""];
    const pPatientId = cpi.data.patientId ? cpi.data.patientId : "";
    const pDate = aryDateTime[0];
    const pTime = aryDateTime[1];
    const pSymptoms = cnr.data.symptoms ? cnr.data.symptoms : "";
    const pMedicalTypeId = cnr.data.medicalTypeId ? cnr.data.medicalTypeId : "";
    
    // 予約種別ごとに入力パラメータ設定
    let apiParams = {};
    if (cnr.type === ReservationType.First) { 
        /* 初診 */ 
        let pFirstName = "";
        let pLastName = "";
        let pBirthdayY = "";
        let pBirthdayM = "";
        let pBirthdayD = "";
        if (cnr.data.name !== undefined) {
            let splitedName = cnr.data.name?.split(" ");
            pLastName = splitedName[0];  // 姓
            pFirstName = splitedName[1]; // 名
        }
        if (cnr.data.birthday !== undefined){
            let splitedBirthday = /(\d+)年(\d+)月(\d+)日/.exec(cnr.data.birthday);
            if (splitedBirthday != null) {
                pBirthdayY = splitedBirthday[1];
                pBirthdayM = splitedBirthday[2];
                pBirthdayD = splitedBirthday[3];
            }
        }

        apiParams = {
            date: pDate,
            time: pTime,
            firstName: pFirstName,
            lastName: pLastName,
            tel: cnr.data.tel ? cnr.data.tel : "",
            birthdayYear: pBirthdayY,
            birthdayMonth: pBirthdayM,
            birthdayDay: pBirthdayD,
            email: cnr.data.email ? cnr.data.email : "",
            memo: pSymptoms,
            medicalTypeId: pMedicalTypeId
        }
    }
    else if (cnr.type === ReservationType.Return || cnr.type === ReservationType.Change) { 
        /* 再診、変更 */ 
        apiParams = {
            date: pDate,
            time: pTime,
            memo: pSymptoms,
            patientId: pPatientId
        }
    }
    else if (cnr.type === ReservationType.ReFirst){
        /* 再初診 */
        apiParams = {
            date: pDate,
            time: pTime,
            memo: pSymptoms,
            medicalTypeId: pMedicalTypeId
        }
    }

    function clickSubmit() {
        if( ua.checkExpiresIn() === LoginStatusType.LoginExpired ){
            if ( cnr.type === ReservationType.First ){
                //ログイン期限切れ
                ua.logout();
            }else{
                //ログアウト、ログイン期限切れ
                ua.logout(function(){history.push("/")});
                return;
            }
        }

        // 共通のコールバック関数
        const callbackFunc = function(res:AxiosResponse<any>){
            setLoadingDone(true);
            if (res?.data) {
                if (res.status === 200) {
                    setErrmess("");
                    cnr.update({done:true, reserveId: res.data.reservationId});
                    history.push("/reservation/complete");
                } else if (res.status === 400) {
                    setErrmess(res.data.message);
                } else if (res.status === 403 || res.status === 404 || res.status >= 500) {
                    cserr.setErrmess(res.data.message);
                } else {
                    cserr.setErrmess(ConstMsg.EG9990001);
                }
            } else {
                cserr.setErrmess(ConstMsg.EG9990001);
            }
        }

        setLoadingDone(false);
        // APIへリクエスト
        let apiUrl = "";
        switch(cnr.type){
            case ReservationType.First:
                // 初診(POSTリクエスト)
                apiUrl = `/c/${clinicId}/reservations`; 
                postApiData(apiUrl, apiParams, callbackFunc);
                break;

            case ReservationType.Return:
            case ReservationType.Change:
                // 再診＆変更(PUTリクエスト)
                apiUrl = `/auth/c/${clinicId}/reservations/${cnr.data.reserveId}`;
                putApiDataWithToken(apiUrl, apiParams, token, callbackFunc);
                break;

            case ReservationType.ReFirst:
                // 再初診(POSTリクエスト)
                apiUrl = `/auth/c/${clinicId}/reservations/patients/${patientNo}`; 
                postApiDataWithToken(apiUrl, apiParams, token, callbackFunc);
                break;

            default:
                break;
        }
    }

    return (
        <>
            <ReservationFlow currentProcess={ProcessName.Confirm}/>
            <ContentsBox>
                {errmess && <MsgBoxErr text={errmess} />}
                <LoadingBox overlay={true} loadingDone={loadingDone}>
                    <ContentsTitle title="以下の内容でご予約をお取りします" />
                    <ReservationInfo />
                    <BtnSubmit title="予約を確定する" onClick={clickSubmit} />
                </LoadingBox>
            </ContentsBox>
            <BtnReturn />
        </>
    );

}